import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { ArrowRight } from 'lucide-react';
import AnimatedBorderCard from './AnimatedBorderCard';

const QuickActionCard = ({ title, description, icon: Icon, onClick, delay = 0, color = 'primary', featured = false }) => {
  const colorStyles = {
    primary: {
      icon: 'text-primary',
      iconBg: 'bg-primary/10',
      hover: 'group-hover:bg-primary/5'
    },
    accent: {
      icon: 'text-[hsl(var(--accent))]',
      iconBg: 'bg-[hsl(var(--accent-light))]',
      hover: 'group-hover:bg-[hsl(var(--accent-light))]'
    },
    success: {
      icon: 'text-[hsl(var(--success))]',
      iconBg: 'bg-[hsl(var(--success-light))]',
      hover: 'group-hover:bg-[hsl(var(--success-light))]'
    }
  };

  const style = colorStyles[color] || colorStyles.primary;

  // Wrapper for neon effect
  if (featured) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay }}
        data-testid={`quick-action-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <AnimatedBorderCard onClick={onClick}>
          <div className="flex items-center gap-6">
            {Icon && (
              <motion.div
                className="relative z-20 flex-shrink-0"
                whileHover={{ scale: 1.03, rotate: 3 }}
                transition={{ type: 'spring', stiffness: 400, damping: 15 }}
              >
                <Icon size={94} className="h-24 w-24" />
              </motion.div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {title}
              </h3>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {description}
              </p>
            </div>
            <motion.div
              className="flex-shrink-0"
              whileHover={{ x: 4 }}
            >
              <ArrowRight className={`h-5 w-5 ${style.icon}`} />
            </motion.div>
          </div>
        </AnimatedBorderCard>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      data-testid={`quick-action-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Card 
        className="border-0 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer group overflow-hidden relative"
        onClick={onClick}
      >
        {/* Gradient overlay on hover - more subtle */}
        <div className={`absolute inset-0 ${style.hover} transition-all duration-300 opacity-0 group-hover:opacity-50`} />
        
        <CardContent className="p-6 relative z-10">
          <div className="flex items-center gap-6">
            {Icon && (
              <motion.div
                className="relative z-20 flex-shrink-0"
                whileHover={{ scale: 1.03, rotate: 3 }}
                transition={{ type: 'spring', stiffness: 400, damping: 15 }}
              >
                <Icon size={94} className="h-24 w-24" />
              </motion.div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {title}
              </h3>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {description}
              </p>
            </div>
            <motion.div
              className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              whileHover={{ x: 4 }}
            >
              <ArrowRight className={`h-5 w-5 ${style.icon}`} />
            </motion.div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default QuickActionCard;